/**
 * Erfolge auf dem Gerät (E13): Zeilen sammeln, rechnen lässt `@kraftwerk/shared`.
 * Flach gelesen, in einem Schritt (Live-Abfragen, CLAUDE.md).
 */

import {
  addDays,
  badges,
  bestPerDay,
  dailyPeriods,
  estimatedOneRepMaxOf,
  foodLogDays,
  isoDateInZone,
  JOKERS_PER_MONTH,
  mondayOf,
  plannedLogId,
  recordDays,
  streak,
  supplementDays,
  trainingOccurrences,
  waterGoalDays,
  weekRings,
} from '@kraftwerk/shared';
import type { Badge, IsoDate, PeriodState, Ring, Streak } from '@kraftwerk/shared';
import { db } from './local.js';
import type { KraftwerkDatabase } from './local.js';
import { readProfile } from './profile.js';

export interface WeekRingsView {
  enabled: boolean;
  from: string;
  to: string;
  rings: Ring[];
}

export async function loadWeekRings(
  timeZone: string,
  database: KraftwerkDatabase = db,
  now: Date = new Date(),
): Promise<WeekRingsView> {
  const today = isoDateInZone(now, timeZone);
  const from = mondayOf(today);
  const [profile, sessions, food, intakes, slots, days] = await Promise.all([
    readProfile(database),
    database.workout_sessions.toArray(),
    database.food_log
      .where('date')
      .between(from, addDays(from as IsoDate, 6), true, true)
      .toArray(),
    database.supplement_intakes.toArray(),
    database.training_slots.toArray(),
    database.training_days.toArray(),
  ]);
  const planned = trainingOccurrences(
    slots.filter((row) => !row.deletedAt),
    days.filter((row) => !row.deletedAt),
    from,
    addDays(from as IsoDate, 6),
    timeZone,
  ).filter((entry) => entry.status === 'planned').length;

  const result = weekRings({
    today,
    timeZone,
    now,
    sessions,
    plannedTrainings: planned,
    food,
    proteinTarget: profile.targets?.protein ?? null,
    intakes,
  });
  return { enabled: profile.achievements, ...result };
}

export interface AchievementsView {
  enabled: boolean;
  supplements: Streak;
  journal: Streak;
  trainingWeeks: Streak;
  badges: Badge[];
}

/**
 * Serien und Abzeichen — alles aus der lokalen Ablage, rückwirkend.
 *
 * Trainingswochen zählen erst ab der Woche, in der es Trainingstage gibt:
 * Die heutige Wochenregel rückwärts auf Wochen davor zu legen, machte aus
 * jeder alten Woche ein Versäumnis.
 */
export async function loadAchievements(
  timeZone: string,
  database: KraftwerkDatabase = db,
  now: Date = new Date(),
): Promise<AchievementsView> {
  const today = isoDateInZone(now, timeZone);
  const [
    profile,
    sessionRows,
    setRows,
    exerciseRows,
    intakes,
    journal,
    food,
    slotRows,
    dayRows,
    planRows,
    water,
  ] = await Promise.all([
    readProfile(database),
    database.workout_sessions.toArray(),
    database.workout_sets.toArray(),
    database.exercises.toArray(),
    database.supplement_intakes.toArray(),
    database.journal_entries.toArray(),
    database.food_log.toArray(),
    database.training_slots.toArray(),
    database.training_days.toArray(),
    database.meal_plan_entries.toArray(),
    database.water_log.toArray(),
  ]);

  const sessions = sessionRows
    .filter((row) => !row.deletedAt)
    .sort((a, b) => a.startedAt.localeCompare(b.startedAt));
  const dayOfSession = new Map(
    sessions.map((row) => [row.id, isoDateInZone(new Date(row.startedAt), timeZone)]),
  );
  const sets = setRows.filter(
    (row) => !row.deletedAt && !row.isWarmup && dayOfSession.has(row.sessionId),
  );

  // Volumen je Einheit.
  const volume = new Map<string, number>();
  for (const set of sets) {
    if (set.weightKg === null || set.reps === null) continue;
    volume.set(set.sessionId, (volume.get(set.sessionId) ?? 0) + set.weightKg * set.reps);
  }

  // Rekordtage über alle Übungen — dieselbe Rechnung wie die Übungsseite.
  const modeOf = new Map(exerciseRows.map((row) => [row.id, row.trackingMode]));
  const estimates = new Map<string, { at: string; value: number }[]>();
  for (const set of sets) {
    const mode = modeOf.get(set.exerciseId);
    if (!mode) continue;
    const value = estimatedOneRepMaxOf(
      {
        weightKg: set.weightKg,
        reps: set.reps,
        seconds: set.seconds,
        distanceM: set.distanceM,
        bodyWeightKg: set.bodyWeightKg,
        isWarmup: set.isWarmup,
      },
      mode,
    );
    if (value === null) continue;
    const list = estimates.get(set.exerciseId) ?? [];
    list.push({ at: set.completedAt ?? '', value });
    estimates.set(set.exerciseId, list);
  }
  const records = [...estimates.values()]
    .flatMap((list) => recordDays(bestPerDay(list, timeZone)).map((point) => point.day))
    .sort();

  // Serien.
  const supplements = streak(supplementDays(intakes, timeZone, now), today, JOKERS_PER_MONTH);
  const journalDone = new Set(
    journal
      .filter(
        (row) =>
          !row.deletedAt &&
          [row.sleepHours, row.energy, row.mood, row.soreness, row.note, row.bodyWeightKg].some(
            (value) => value !== null && value !== undefined && value !== '',
          ),
      )
      .map((row) => row.date),
  );
  const firstJournal = [...journalDone].sort()[0];
  const journalStreak = firstJournal
    ? streak(dailyPeriods(journalDone, firstJournal, today), today, JOKERS_PER_MONTH)
    : { current: 0, best: 0, jokersUsed: 0 };

  const slots = slotRows.filter((row) => !row.deletedAt);
  const days = dayRows.filter((row) => !row.deletedAt);
  const slotStart = slots
    .map((row) => (row as { createdAt?: string }).createdAt ?? row.updatedAt)
    .sort()[0];
  const weeks: { key: string; state: PeriodState }[] = [];
  const currentWeek = mondayOf(today);
  if (slotStart) {
    for (
      let week = mondayOf(isoDateInZone(new Date(slotStart), timeZone));
      week <= currentWeek;
      week = addDays(week as IsoDate, 7)
    ) {
      const end = addDays(week as IsoDate, 6);
      const planned = trainingOccurrences(slots, days, week, end, timeZone).filter(
        (entry) => entry.status === 'planned',
      ).length;
      const done = sessions.filter((row) => {
        const day = dayOfSession.get(row.id)!;
        return day >= week && day <= end;
      }).length;
      weeks.push({ key: week, state: planned === 0 ? 'neutral' : done >= planned ? 'ok' : 'miss' });
    }
  }
  const trainingWeeks = streak(weeks, currentWeek);

  return {
    enabled: profile.achievements,
    supplements,
    journal: journalStreak,
    trainingWeeks,
    badges: badges({
      sessionDays: sessions.map((row) => dayOfSession.get(row.id)!),
      bestSessionVolumeKg: Math.max(0, ...volume.values()),
      recordDays: records,
      bestSupplementStreak: supplements.best,
      bestTrainingWeekStreak: trainingWeeks.best,
      distinctRecipesEaten: new Set(
        food.filter((row) => !row.deletedAt && row.recipeId).map((row) => row.recipeId),
      ).size,
      journalDays: journalDone.size,
      foodDays: foodLogDays(food, new Set(planRows.map((row) => plannedLogId(row.id))), today),
      waterDays: waterGoalDays(water, profile.water.targetMl, today),
    }),
  };
}
